import { useCallback, useContext } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Instrument,
  InvestmentHolding,
  InvestmentHoldingCollection,
} from "wealthmode-frontend/backend"
import { Box, HStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoney } from "wealthmode-frontend/util"

import { DataTable } from "@/components"
import { BackendContext } from "@/context"

export default function InvestmentHoldings() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const holdings = data.link("investment_holdings")?.resolve() as InvestmentHoldingCollection | undefined
    if (!holdings) {
      return undefined
    }

    await holdings.fetch({ reload: true })

    return holdings
  }, [backend, client_id, account_id])

  return (
    <Box>
      <HStack>
        <Box className="flex-1">
          <DataTable
            title={"Investment Holdings"}
            load={load}
            header={[
              { id: "id", title: "ID", shrink: true },
              { id: "symbol", title: "Symbol" },
              { id: "name", title: "Name" },
              { id: "type", title: "Type" },
              { id: "quantity", title: "Qty" },
              { id: "costbasis", title: "Cost Basis" },
              { id: "value", title: "Value" },
              { id: "pnl", title: "PNL" },
            ]}
            render={(model: InvestmentHolding, field: string) => {
              const sec = model.embed("instrument") as Instrument | null
              switch (field) {
                case "symbol":
                  if (sec == null) {
                    return "?"
                  }
                  return <Link href={`/instruments/${sec.attributes.id}`}>{sec.attributes.symbol || "?"}</Link>
                case "name":
                  return sec?.attributes.name || "?"
                case "type":
                  return sec?.attributes.type || ""
                case "pnl":
                  const change = +model.attributes.price - +model.attributes.costbasis
                  return formatMoney(+model.attributes.quantity * change)
                case "value":
                  return formatMoney(+model.attributes.value)
              }
              return model.get(field)
            }}
          />
        </Box>
      </HStack>
    </Box>
  )
}
